import { AvisoErrorExportacion } from '@/components/dominio/AvisoErrorExportacion';
import {
  type PropsExcluidas,
  SelectorObrasExcluidas,
} from '@/components/dominio/SelectorObrasExcluidas';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useViviendaCuadrillas } from '@/hooks/useAvanceMatriz';
import { useExportacion } from '@/hooks/useExportacion';
import { useFiltrosGlobales } from '@/lib/FiltrosGlobalesContext';
import type { FilaTabla, ReporteTablaExport, TablaExport } from '@/lib/exportarViviendaGerencia';
import {
  describirExcluidas,
  describirPeriodo,
  fmtMonto,
  fmtNum,
  fmtPct,
  razon,
} from '@/lib/formatoGerencia';
import { CaretDown, CaretRight, CircleNotch, DownloadSimple, FilePdf } from '@phosphor-icons/react';
import { type ReactElement, type ReactNode, useMemo, useState } from 'react';

/** Estándar teórico de M.O. por m² (el mismo de Costos/m²). Editable en el informe. */
const MO_ESTANDAR_DEFAULT = 122000;
/** Etiqueta de la M.O. de obras/partidas sin horas registradas (debe coincidir con la API). */
const SIN_HORAS = '(Sin horas registradas)';

type Dim = 'partida' | 'subpartida' | 'cuadrilla';
const DIMS: { id: Dim; titulo: string }[] = [
  { id: 'partida', titulo: 'Partida' },
  { id: 'subpartida', titulo: 'Subpartida' },
  { id: 'cuadrilla', titulo: 'Cuadrilla' },
];
const tituloDim = (d: Dim) => DIMS.find((x) => x.id === d)?.titulo ?? d;

/** Acumulado de un nodo del árbol. */
interface Acum {
  horas: number;
  mo: number;
  /** M.O. solo de obras con m² (para compararla con el presupuesto teórico). */
  moConArea: number;
  obras: Set<string>;
  /** Partidas (task_no) en las que participa el nodo: base del "peso en la partida". */
  partidas: Set<string>;
}
const nuevo = (): Acum => ({
  horas: 0,
  mo: 0,
  moConArea: 0,
  obras: new Set(),
  partidas: new Set(),
});

interface Nodo {
  key: string;
  etiqueta: string;
  /** Valor crudo de la dimensión (para ordenar y detectar "sin horas"). */
  valor: string;
  acum: Acum;
  hijos: Map<string, Nodo>;
}

/** Datos compartidos por las celdas de todas las tablas. */
interface Ctx {
  total: Acum;
  m2: number;
  presupuesto: number;
  /** M.O. total de cada partida (todas las obras incluidas, cuadrillas y "sin horas"). */
  totalPartida: Map<string, number>;
}

const natural = (a: string, b: string) => a.localeCompare(b, 'es', { numeric: true });

/**
 * Peso del nodo dentro de su(s) partida(s): M.O. del nodo ÷ M.O. total de las partidas en
 * las que participa. Con la partida como nivel superior es la fracción de esa partida; si
 * la partida está debajo (p. ej. Cuadrilla primero) es cuánto de las partidas donde trabaja
 * lo carga esa cuadrilla.
 */
function pesoEnPartida(a: Acum, ctx: Ctx): number | null {
  let den = 0;
  for (const p of a.partidas) den += ctx.totalPartida.get(p) ?? 0;
  return razon(a.mo, den);
}

/** Valores de una fila, en el mismo orden que las columnas. */
function valoresDe(a: Acum, ctx: Ctx): (number | null)[] {
  return [
    a.horas || null,
    a.mo || null,
    razon(a.mo, ctx.total.mo),
    pesoEnPartida(a, ctx),
    a.horas > 0 ? a.mo / a.horas : null,
    ctx.m2 > 0 && a.moConArea ? a.moConArea / ctx.m2 : null,
    razon(a.moConArea, ctx.presupuesto),
    a.obras.size || null,
  ];
}

/** Celdas (sin la primera) de una fila de la tabla. */
function celdasDe(a: Acum, ctx: Ctx): ReactElement[] {
  const pct = 'bg-emerald-50/60 text-right text-xs font-semibold tabular-nums';
  const num = 'text-right text-xs tabular-nums';
  return [
    <TableCell key="h" className={num}>
      {a.horas ? fmtNum(a.horas) : '—'}
    </TableCell>,
    <TableCell key="mo" className={num}>
      {fmtMonto(a.mo)}
    </TableCell>,
    <TableCell key="p1" className={pct}>
      {fmtPct(a.mo, ctx.total.mo)}
    </TableCell>,
    <TableCell key="pp" className={pct}>
      {(() => {
        const v = pesoEnPartida(a, ctx);
        return v == null ? '—' : fmtPct(v, 1);
      })()}
    </TableCell>,
    <TableCell key="th" className={num}>
      {a.horas > 0 ? fmtMonto(a.mo / a.horas) : '—'}
    </TableCell>,
    <TableCell key="m2" className={num}>
      {ctx.m2 > 0 ? fmtMonto(a.moConArea / ctx.m2) : '—'}
    </TableCell>,
    <TableCell key="p2" className={pct}>
      {fmtPct(a.moConArea, ctx.presupuesto)}
    </TableCell>,
    <TableCell key="ob" className={num}>
      {a.obras.size || '—'}
    </TableCell>,
  ];
}

const COLUMNAS_UI = [
  'Horas',
  'M.O. real',
  '% del M.O. real',
  'Peso en la partida',
  '₡ por hora',
  'M.O. por m²',
  '% del presupuesto de M.O.',
  'Obras',
];

/** Título de columnas para exportar (el PDF no trae el símbolo ₡). */
const COLUMNAS_EXPORT: { titulo: string; tipo: 'num' | 'monto' | 'pct' }[] = [
  { titulo: 'Horas', tipo: 'num' },
  { titulo: 'M.O. real', tipo: 'monto' },
  { titulo: '% del M.O. real', tipo: 'pct' },
  { titulo: 'Peso en la partida', tipo: 'pct' },
  { titulo: 'Colones por hora', tipo: 'monto' },
  { titulo: 'M.O. por m²', tipo: 'monto' },
  { titulo: '% del presupuesto de M.O.', tipo: 'pct' },
  { titulo: 'Obras', tipo: 'num' },
];

type FilaCuad = {
  worksNo: string;
  taskNo: string;
  subactividad: string;
  cuadrilla: string;
  horas: number;
  mo: number;
};

/** Árbol según los niveles elegidos (de arriba hacia abajo). */
function construirArbol(
  filas: FilaCuad[],
  niveles: Dim[],
  areaDe: Map<string, number>,
  nombrePartida: Map<string, string>,
): Map<string, Nodo> {
  const raiz = new Map<string, Nodo>();
  const valorDe = (f: FilaCuad, d: Dim) =>
    d === 'partida' ? f.taskNo : d === 'subpartida' ? f.subactividad : f.cuadrilla;
  const etiquetaDe = (d: Dim, v: string) => (d === 'partida' ? (nombrePartida.get(v) ?? v) : v);
  for (const f of filas) {
    const area = areaDe.get(f.worksNo) ?? 0;
    let nivel = raiz;
    let ruta = '';
    for (const d of niveles) {
      const v = valorDe(f, d);
      ruta += `\u0001${d}:${v}`;
      let n = nivel.get(v);
      if (!n) {
        n = { key: ruta, etiqueta: etiquetaDe(d, v), valor: v, acum: nuevo(), hijos: new Map() };
        nivel.set(v, n);
      }
      n.acum.horas += f.horas;
      n.acum.mo += f.mo;
      if (area > 0) n.acum.moConArea += f.mo;
      n.acum.obras.add(f.worksNo);
      n.acum.partidas.add(f.taskNo);
      nivel = n.hijos;
    }
  }
  return raiz;
}

const ordenar = (nodos: Nodo[], orden: 'peso' | 'nombre') =>
  [...nodos].sort((a, b) => {
    // "(Sin horas registradas)" siempre al final.
    const sa = a.valor === SIN_HORAS ? 1 : 0;
    const sb = b.valor === SIN_HORAS ? 1 : 0;
    if (sa !== sb) return sa - sb;
    return orden === 'peso' ? b.acum.mo - a.acum.mo : natural(a.valor, b.valor);
  });

/** Aplana un árbol a filas de exportación (nivel = profundidad). */
function aFilasExport(
  arbol: Map<string, Nodo>,
  niveles: Dim[],
  ctx: Ctx,
  orden: 'peso' | 'nombre',
): FilaTabla[] {
  const filas: FilaTabla[] = [];
  const rec = (nodos: Map<string, Nodo>, p: number) => {
    for (const n of ordenar([...nodos.values()], orden)) {
      filas.push({ nivel: p, etiqueta: n.etiqueta, valores: valoresDe(n.acum, ctx) });
      if (p < niveles.length - 1) rec(n.hijos, p + 1);
    }
  };
  rec(arbol, 0);
  filas.push({ nivel: 0, total: true, etiqueta: 'Total', valores: valoresDe(ctx.total, ctx) });
  return filas;
}

const caret = (abierto: boolean) =>
  abierto ? (
    <CaretDown className="h-3 w-3" weight="bold" />
  ) : (
    <CaretRight className="h-3 w-3" weight="bold" />
  );

/** Una tabla jerárquica expandible. Al cambiar los niveles se colapsa (sin volver a montarse). */
function TablaJerarquica({
  titulo,
  controles,
  arbol,
  niveles,
  ctx,
  orden,
}: {
  titulo: string;
  controles: ReactNode;
  arbol: Map<string, Nodo>;
  niveles: Dim[];
  ctx: Ctx;
  orden: 'peso' | 'nombre';
}) {
  // Lo expandido se asocia a la configuración de niveles: si cambia, vuelve a estar colapsado.
  const firma = niveles.join('>');
  const [est, setEst] = useState<{ firma: string; set: Set<string> }>({
    firma,
    set: new Set(),
  });
  const expandidos = est.firma === firma ? est.set : new Set<string>();
  const setExpandidos = (set: Set<string>) => setEst({ firma, set });
  const toggle = (key: string) => {
    const next = new Set(expandidos);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpandidos(next);
  };

  const filas: ReactElement[] = [];
  const recorrer = (nodos: Map<string, Nodo>, profundidad: number) => {
    for (const n of ordenar([...nodos.values()], orden)) {
      const hoja = profundidad === niveles.length - 1;
      const abierto = expandidos.has(n.key);
      const fondo =
        profundidad === 0
          ? 'bg-emerald-100 font-semibold'
          : profundidad === 1
            ? 'bg-emerald-50 font-medium'
            : '';
      const fondoSticky =
        profundidad === 0
          ? 'bg-emerald-100'
          : profundidad === 1
            ? 'bg-emerald-50'
            : 'bg-background';
      filas.push(
        <TableRow key={n.key} className={fondo}>
          <TableCell className={`sticky left-0 z-10 text-xs ${fondoSticky}`}>
            <div style={{ paddingLeft: `${profundidad * 1.25}rem` }}>
              {hoja ? (
                <span className="pl-4">{n.etiqueta}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => toggle(n.key)}
                  className="flex items-center gap-1 text-left hover:underline"
                >
                  {caret(abierto)}
                  {n.etiqueta}
                </button>
              )}
            </div>
          </TableCell>
          {celdasDe(n.acum, ctx)}
        </TableRow>,
      );
      if (!hoja && abierto) recorrer(n.hijos, profundidad + 1);
    }
  };
  recorrer(arbol, 0);

  const claves = () => {
    const k = new Set<string>();
    const rec = (nodos: Map<string, Nodo>, p: number) => {
      for (const n of nodos.values()) {
        if (p < niveles.length - 1) {
          k.add(n.key);
          rec(n.hijos, p + 1);
        }
      }
    };
    rec(arbol, 0);
    return k;
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2 text-xs">
          <span className="text-sm font-semibold">{titulo}</span>
          <span className="text-zinc-300">|</span>
          {controles}
          <span className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setExpandidos(claves())}
              className="text-blue-600 hover:underline"
            >
              Expandir todo
            </button>
            <span className="text-zinc-300">|</span>
            <button
              type="button"
              onClick={() => setExpandidos(new Set())}
              className="text-blue-600 hover:underline"
            >
              Colapsar
            </button>
          </span>
        </div>
        <Table wrapperClassName="max-h-[70vh]">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 top-0 z-30 min-w-[300px] bg-background">
                {niveles.map(tituloDim).join(' / ')}
              </TableHead>
              {COLUMNAS_UI.map((c) => (
                <TableHead
                  key={c}
                  className="sticky top-0 z-20 min-w-[110px] bg-background text-right text-xs"
                >
                  {c}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filas}
            <TableRow className="border-t-2 border-zinc-300 bg-zinc-100 font-bold">
              <TableCell className="sticky left-0 z-10 bg-zinc-100 text-xs">Total</TableCell>
              {celdasDe(ctx.total, ctx)}
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function ViviendaCuadrillas({ excluidas, onExcluidas }: PropsExcluidas) {
  const { data, isLoading, error } = useViviendaCuadrillas();
  const { filtros } = useFiltrosGlobales();
  // Tabla 1: niveles configurables. Tabla 2: Cuadrilla primero (con o sin partida).
  const [niveles, setNiveles] = useState<Dim[]>(['partida', 'subpartida', 'cuadrilla']);
  const [conPartida, setConPartida] = useState(true);
  const [orden, setOrden] = useState<'peso' | 'nombre'>('peso');
  const [estandar, setEstandar] = useState<number>(MO_ESTANDAR_DEFAULT);
  const { exportando, error: errorExport, ejecutar, limpiarError } = useExportacion();
  const niveles2: Dim[] = conPartida
    ? ['cuadrilla', 'partida', 'subpartida']
    : ['cuadrilla', 'subpartida'];

  const resp = data?.data;
  const filas = useMemo(
    () => (resp?.filas ?? []).filter((f) => !excluidas.has(f.worksNo)),
    [resp, excluidas],
  );
  const areaDe = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of resp?.obras ?? []) m.set(o.worksNo, o.areaM2 ?? 0);
    return m;
  }, [resp]);
  const filasSelector = useMemo(() => {
    const nombre = new Map((resp?.obras ?? []).map((o) => [o.worksNo, o.displayName] as const));
    return filas.map((f) => ({
      worksNo: f.worksNo,
      displayName: nombre.get(f.worksNo) ?? null,
      taskNo: f.taskNo,
      costo: f.mo,
    }));
  }, [resp, filas]);
  const nombrePartida = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of resp?.partidas ?? []) m.set(p.taskNo, p.tareaDisplay ?? p.taskNo);
    return m;
  }, [resp]);

  // Totales, cobertura de horas por obra y M.O. total por partida.
  const resumen = useMemo(() => {
    const total = nuevo();
    const porObra = new Map<string, { mo: number; sinHoras: number }>();
    const totalPartida = new Map<string, number>();
    let sinHoras = 0;
    for (const f of filas) {
      const area = areaDe.get(f.worksNo) ?? 0;
      total.horas += f.horas;
      total.mo += f.mo;
      if (area > 0) total.moConArea += f.mo;
      total.obras.add(f.worksNo);
      total.partidas.add(f.taskNo);
      totalPartida.set(f.taskNo, (totalPartida.get(f.taskNo) ?? 0) + f.mo);
      const o = porObra.get(f.worksNo) ?? { mo: 0, sinHoras: 0 };
      o.mo += f.mo;
      if (f.cuadrilla === SIN_HORAS) {
        o.sinHoras += f.mo;
        sinHoras += f.mo;
      }
      porObra.set(f.worksNo, o);
    }
    let m2 = 0;
    let sinArea = 0;
    for (const w of total.obras) {
      const a = areaDe.get(w) ?? 0;
      if (a > 0) m2 += a;
      else sinArea++;
    }
    return { total, porObra, totalPartida, sinHoras, m2, sinArea };
  }, [filas, areaDe]);

  const ctx: Ctx = {
    total: resumen.total,
    m2: resumen.m2,
    presupuesto: estandar * resumen.m2,
    totalPartida: resumen.totalPartida,
  };

  const arbol1 = useMemo(
    () => construirArbol(filas, niveles, areaDe, nombrePartida),
    [filas, niveles, areaDe, nombrePartida],
  );
  const arbol2 = useMemo(
    () =>
      construirArbol(
        filas,
        conPartida ? ['cuadrilla', 'partida', 'subpartida'] : ['cuadrilla', 'subpartida'],
        areaDe,
        nombrePartida,
      ),
    [filas, conPartida, areaDe, nombrePartida],
  );

  // Configuración de niveles de la tabla 1: cada selector elige una dimensión; no se repiten.
  const setNivel = (i: number, v: Dim | '') => {
    setNiveles((prev) => {
      if (v === '') return prev.slice(0, i); // quita este nivel y los más profundos
      const j = prev.indexOf(v);
      if (i >= prev.length) return j >= 0 ? prev : [...prev, v]; // nivel nuevo (sin repetir)
      const next = [...prev];
      const anterior = prev[i];
      if (j >= 0 && j !== i && anterior) next[j] = anterior; // ya se usa en otro nivel → se intercambian
      next[i] = v;
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
      </div>
    );
  }
  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const { total, m2, sinArea, sinHoras } = resumen;
  const presupuesto = ctx.presupuesto;
  const moPorM2 = m2 > 0 ? total.moConArea / m2 : 0;

  // ---- Exportación: las dos tablas (el Excel lleva todos los niveles; el PDF hasta el 2.º) ----
  const aTabla = (titulo: string, nv: Dim[], arbol: Map<string, Nodo>): TablaExport => ({
    titulo,
    arbol: true,
    nivelMaxPdf: Math.min(nv.length - 1, 1),
    encabezado: nv.map(tituloDim).join(' / '),
    columnas: COLUMNAS_EXPORT,
    filas: aFilasExport(arbol, nv, ctx, orden),
  });

  const armarReporte = (): ReporteTablaExport => {
    const hoy = new Date().toLocaleDateString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const filtrosActivos = [
      filtros.filtroProyecto && `Proyecto: ${filtros.filtroProyecto}`,
      filtros.filtroBloque && `Bloque: ${filtros.filtroBloque}`,
      filtros.filtroObra && `Obra: ${filtros.filtroObra}`,
      filtros.filtroModelo && `Modelo: ${filtros.filtroModelo}`,
      filtros.estadosSel?.length && `Estado de venta actual: ${filtros.estadosSel.join(', ')}`,
      describirExcluidas(excluidas, resp?.filas ?? []),
    ].filter(Boolean);

    return {
      titulo: 'Vivienda Gerencia · Costo por cuadrilla',
      subtitulo: [
        `Viviendas VB, VC y VN (área PRO VIVIENDA) · Tabla 1: ${niveles.map(tituloDim).join(' > ')} · Tabla 2: ${niveles2.map(tituloDim).join(' > ')} · Estándar de M.O.: ${fmtNum(estandar)} colones por m²`,
        `Período: ${describirPeriodo(filtros)} · Generado el ${hoy}${filtrosActivos.length ? ` · ${filtrosActivos.join(' · ')}` : ''}`,
        'M.O. real pagada en BC repartida entre subpartidas y cuadrillas según las horas del Control de horas. Peso en la partida = M.O. ÷ M.O. total de la partida. Presupuesto teórico de M.O. = estándar × m² de las casas.',
      ],
      kpis: [
        { titulo: 'M.O. real', valor: fmtMonto(total.mo) },
        { titulo: 'Presupuesto teórico de M.O.', valor: fmtMonto(presupuesto) },
        { titulo: 'M.O. real ÷ presupuesto', valor: fmtPct(total.moConArea, presupuesto) },
        { titulo: 'M.O. real por m²', valor: fmtMonto(moPorM2) },
        { titulo: 'Horas', valor: fmtNum(total.horas) },
        { titulo: 'Obras', valor: String(total.obras.size) },
      ],
      tabla: aTabla('Costo por cuadrilla', niveles, arbol1),
      tablasExtra: [aTabla('Cuadrilla primero', niveles2, arbol2)],
      nombreArchivo: `vivienda-costo-cuadrilla-${new Date().toISOString().slice(0, 10)}`,
    };
  };

  const exportar = (formato: 'xlsx' | 'pdf') =>
    ejecutar(formato, async () => {
      const rep = armarReporte();
      const lib = await import('@/lib/exportarViviendaGerencia');
      if (formato === 'xlsx') await lib.exportarTablaExcel(rep);
      else await lib.exportarTablaPdf(rep);
    });

  const tarjetas = [
    { titulo: 'M.O. real', valor: fmtMonto(total.mo), sub: 'Pagada en BC (cuenta 6010060000000)' },
    {
      titulo: 'Presupuesto teórico de M.O.',
      valor: fmtMonto(presupuesto),
      sub: `₡${fmtNum(estandar)} × ${fmtNum(m2)} m²`,
    },
    {
      titulo: 'M.O. real ÷ presupuesto',
      valor: fmtPct(total.moConArea, presupuesto),
      sub: `M.O. real por m²: ${fmtMonto(moPorM2)}`,
      destacada: true,
    },
    {
      titulo: 'Horas registradas',
      valor: fmtNum(total.horas),
      sub: `₡ por hora: ${total.horas > 0 ? fmtMonto(total.mo / total.horas) : '—'}`,
    },
    {
      titulo: 'M.O. sin horas',
      valor: fmtPct(sinHoras, total.mo),
      sub: 'Pagada pero sin horas en el Control de horas',
      destacada: sinHoras > 0 && sinHoras / (total.mo || 1) > 0.05,
    },
    {
      titulo: 'Obras',
      valor: String(total.obras.size),
      sub: sinArea > 0 ? `${sinArea} sin m² (fuera del presupuesto)` : 'Con M.O. u horas',
    },
  ];

  const botonExport =
    'inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50';
  const selectCls = 'rounded border border-zinc-300 bg-white px-1.5 py-1 text-zinc-800';

  const controlesTabla1 = (
    <>
      <span className="text-muted-foreground">Niveles:</span>
      {[0, 1, 2].map((i) => {
        if (i > niveles.length) return null; // el siguiente nivel aparece al completar el anterior
        const actual = niveles[i] ?? '';
        return (
          <select
            key={i}
            value={actual}
            onChange={(e) => setNivel(i, e.target.value as Dim | '')}
            className={selectCls}
            aria-label={`Nivel ${i + 1}`}
          >
            {i > 0 && <option value="">(ninguno)</option>}
            {DIMS.map((d) => (
              <option key={d.id} value={d.id}>
                {i + 1}. {d.titulo}
              </option>
            ))}
          </select>
        );
      })}
    </>
  );

  const controlesTabla2 = (
    <label className="flex cursor-pointer items-center gap-1.5 text-zinc-700">
      <input
        type="checkbox"
        checked={conPartida}
        onChange={(e) => setConPartida(e.target.checked)}
        className="h-3.5 w-3.5"
      />
      Incluir partida
      <span className="text-muted-foreground">
        ({conPartida ? 'Cuadrilla > Partida > Subpartida' : 'Cuadrilla > Subpartida'})
      </span>
    </label>
  );

  return (
    <div className="space-y-4">
      {errorExport && <AvisoErrorExportacion error={errorExport} onCerrar={limpiarError} />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        {tarjetas.map((c) => (
          <Card key={c.titulo} className={c.destacada ? 'border-emerald-300 bg-emerald-50/60' : ''}>
            <CardContent className="p-4">
              <div className="text-xs font-medium text-muted-foreground">{c.titulo}</div>
              <div className="mt-1 text-xl font-semibold tabular-nums">{c.valor}</div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">{c.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Controles comunes a las dos tablas */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 px-4 py-2 text-xs">
          <label className="flex items-center gap-1 text-muted-foreground">
            Orden:
            <select
              value={orden}
              onChange={(e) => setOrden(e.target.value as 'peso' | 'nombre')}
              className={selectCls}
            >
              <option value="peso">Mayor peso primero</option>
              <option value="nombre">Por nombre / código</option>
            </select>
          </label>
          <span className="text-zinc-300">|</span>
          <label className="flex items-center gap-1 text-muted-foreground">
            Estándar M.O. ₡/m²:
            <input
              type="number"
              min={0}
              step={1000}
              value={estandar}
              onChange={(e) => setEstandar(Math.max(0, Number(e.target.value) || 0))}
              className="w-24 rounded border border-zinc-300 px-1.5 py-1 text-right text-zinc-800"
            />
          </label>
          <span className="text-zinc-300">|</span>
          <SelectorObrasExcluidas
            filas={filasSelector}
            excluidas={excluidas}
            onChange={onExcluidas}
            tituloMonto="M.O. real"
            extra={{
              titulo: 'Cobertura de horas',
              texto: (w) => {
                const o = resumen.porObra.get(w);
                if (!o || o.mo <= 0) return '—';
                return o.mo - o.sinHoras > 0 ? fmtPct(o.mo - o.sinHoras, o.mo) : '0,0 %';
              },
              valor: (w) => {
                const o = resumen.porObra.get(w);
                return o && o.mo > 0 ? (o.mo - o.sinHoras) / o.mo : 1;
              },
            }}
          />
          <span className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => exportar('xlsx')}
              disabled={exportando !== null}
              className={botonExport}
            >
              <DownloadSimple className="h-3.5 w-3.5" weight="bold" />
              {exportando === 'xlsx' ? 'Generando…' : 'Exportar a Excel'}
            </button>
            <button
              type="button"
              onClick={() => exportar('pdf')}
              disabled={exportando !== null}
              className={botonExport}
            >
              <FilePdf className="h-3.5 w-3.5" weight="bold" />
              {exportando === 'pdf' ? 'Generando…' : 'Exportar a PDF'}
            </button>
          </span>
        </CardContent>
      </Card>

      <TablaJerarquica
        titulo="Costo por cuadrilla"
        controles={controlesTabla1}
        arbol={arbol1}
        niveles={niveles}
        ctx={ctx}
        orden={orden}
      />

      <TablaJerarquica
        titulo="Cuadrilla primero"
        controles={controlesTabla2}
        arbol={arbol2}
        niveles={niveles2}
        ctx={ctx}
        orden={orden}
      />

      <p className="text-[11px] text-muted-foreground">
        La M.O. real es la pagada en BC por obra y partida; se reparte entre subpartidas y
        cuadrillas según las horas del Control de horas en esa misma obra y partida (en el período
        elegido). Lo pagado en partidas sin horas queda en «{SIN_HORAS}».{' '}
        <strong>Peso en la partida</strong>: M.O. de la fila ÷ M.O. total de la partida; si la
        partida es el primer nivel es su fracción de la partida, y si la fila agrupa varias partidas
        (p. ej. una cuadrilla) es cuánto carga esa cuadrilla de las partidas donde trabaja. El
        presupuesto teórico es el estándar de M.O. por m² por los m² de las casas; las obras sin m²
        no entran a esa comparación. Con el selector «Obras» podés excluir casas con información
        incompleta (la columna «Cobertura de horas» ayuda a identificarlas). El PDF llega hasta el
        segundo nivel; el Excel incluye todos.
      </p>
    </div>
  );
}
